<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';

const props = defineProps({
  contact: { type: Object, default: null },
});

const emit = defineEmits(['close', 'updated']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const contactsFormRef = ref(null);
const contactData = ref(null);
const showDeleteSection = ref(false);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const open = () => {
  if (props.contact) {
    contactData.value = {
      id: props.contact.id,
      name: props.contact.name,
      email: props.contact.email,
      phoneNumber: props.contact.phoneNumber,
      additionalAttributes: props.contact.additionalAttributes || {},
      customAttributes: props.contact.customAttributes || {},
    };
  }
  showDeleteSection.value = false;
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
  emit('close');
};

const handleFormUpdate = updatedData => {
  contactData.value = updatedData;
};

const handleUpdate = async () => {
  if (!contactData.value) return;

  try {
    await store.dispatch('contacts/update', contactData.value);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
    emit('updated', contactData.value);
    close();
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

watch(
  () => props.contact,
  newContact => {
    if (newContact) {
      contactData.value = {
        id: newContact.id,
        name: newContact.name,
        email: newContact.email,
        phoneNumber: newContact.phoneNumber,
        additionalAttributes: newContact.additionalAttributes || {},
        customAttributes: newContact.customAttributes || {},
      };
    }
  },
  { immediate: true }
);

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    surface="light"
    class="contact-edit-dialog"
    @close="emit('close')"
  >
    <template #header>
      <div class="dialog-header">
        <div class="contact-header-info">
          <Avatar
            :name="contact?.name"
            :src="contact?.thumbnail"
            :size="48"
            :status="contact?.availabilityStatus"
            hide-offline-status
            rounded-full
          />
          <div class="contact-header-text">
            <h2 class="contact-dialog-title">
              {{ contact?.name || 'Edit Contact' }}
            </h2>
            <p v-if="contact?.email" class="contact-dialog-email">
              {{ contact.email }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <div class="dialog-content">
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        @update="handleFormUpdate"
      />

      <div class="delete-section-toggle">
        <button
          class="toggle-delete-btn"
          @click="showDeleteSection = !showDeleteSection"
        >
          <span class="i-ph-warning-circle-bold size-4" />
          {{
            showDeleteSection ? 'Hide delete options' : 'Show delete options'
          }}
          <span
            :class="
              showDeleteSection ? 'i-ph-caret-up-bold' : 'i-ph-caret-down-bold'
            "
            class="size-4"
          />
        </button>
      </div>

      <div v-if="showDeleteSection" class="delete-section">
        <ContactDeleteSection
          :selected-contact="{
            id: contact?.id,
            name: contact?.name,
          }"
        />
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <button class="cancel-btn" @click="close">
          {{ t('DIALOG.BUTTONS.CANCEL') }}
        </button>
        <button
          class="submit-btn"
          :disabled="isUpdating || isFormInvalid"
          @click="handleUpdate"
        >
          <span v-if="isUpdating" class="loading-spinner-sm" />
          {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON') }}
        </button>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.contact-edit-dialog :deep(dialog::backdrop) {
  background: rgba(17, 24, 39, 0.18) !important;
  backdrop-filter: blur(4px);
}

.contact-edit-dialog {
  /* Force light WLAB palette inside dialog even if body is dark */
  --wlab-bg-primary: #ffffff;
  --wlab-bg-secondary: #f8fafc;
  --wlab-bg-tertiary: #f1f5f9;
  --wlab-bg-input: #ffffff;
  --wlab-bg-hover: #f1f5f9;
  --wlab-bg-card: #ffffff;

  --wlab-text-primary: #111827;
  --wlab-text-secondary: #374151;
  --wlab-text-tertiary: #6b7280;
  --wlab-text-muted: #94a3b8;

  --wlab-border-default: #e5e7eb;
  --wlab-border-light: #f1f5f9;
  --wlab-border-focus: #0066ff;
  --wlab-accent: #0066ff;
  --wlab-accent-light: #fff7ed;
}

.contact-edit-dialog :deep(.dialog-form--light) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

.contact-edit-dialog :deep(.text-n-slate-12) {
  color: #111827 !important;
}

.contact-edit-dialog :deep(.text-n-slate-11),
.contact-edit-dialog :deep(.text-n-slate-10),
.contact-edit-dialog :deep(.text-n-slate-9) {
  color: #4b5563 !important;
}

.contact-edit-dialog :deep(.bg-n-alpha-2),
.contact-edit-dialog :deep(.bg-n-alpha-3),
.contact-edit-dialog :deep(.bg-n-alpha-black2),
.contact-edit-dialog :deep(.bg-n-solid-2),
.contact-edit-dialog :deep(.bg-n-solid-1) {
  background: #ffffff !important;
}

.contact-edit-dialog :deep(.text-n-brand) {
  color: #0066ff !important;
}

.contact-edit-dialog :deep(input),
.contact-edit-dialog :deep(select),
.contact-edit-dialog :deep(textarea) {
  background: #ffffff !important;
  color: #111827 !important;
}

.contact-edit-dialog :deep(input::placeholder),
.contact-edit-dialog :deep(textarea::placeholder) {
  color: #6b7280 !important;
}

.contact-edit-dialog :deep(.border-n-weak),
.contact-edit-dialog :deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}

.dialog-header {
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
}

.contact-header-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.contact-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.contact-dialog-email {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.delete-section-toggle {
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.toggle-delete-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #6b7280;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.toggle-delete-btn:hover {
  background: var(--wlab-bg-input);
  color: #111827;
}

.delete-section {
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
}

.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.cancel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  color: #000000;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cancel-btn:hover {
  background: #f8fafc;
  color: #111827;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  background-color: #fff;
  color: #000;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  background-color: #f0f0f0;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
